// CareLogix™ ORACLE — AdminOne Market Abuse Awareness Center™ (Phase 33 §4)
// DEFENSIVE intelligence only. Detect, review, and resolve flagged market-abuse events.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";
import { adminMarketAbuseApi, guardApi } from "@/api/phase33Client";

const SEVERITY_COLOR = {
  critical: PALETTE.red,
  high:     "#fb7185",
  medium:   PALETTE.amber,
  low:      PALETTE.gold,
};

function Tab({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...S.tab, ...(active ? S.tabActive : null) }}
      data-testid={testid}
    >{children}</button>
  );
}

export default function AdminMarketAbuseAwareness({ testid = "admin-market-abuse-awareness" }) {
  const [resolved, setResolved] = useState(false);
  const [severity, setSeverity] = useState("");
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);
  const [demoText, setDemoText] = useState("");
  const [demoResult, setDemoResult] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { resolved, limit: 50 };
      if (severity) params.severity = severity;
      const r = await adminMarketAbuseApi.events(params);
      setItems(r.data?.events || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setError("AdminOne authentication required.");
      else setError("Could not load Market Abuse events.");
    } finally {
      setLoading(false);
    }
  }, [resolved, severity]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    guardApi.catalog().then((r) => setCatalog(r.data)).catch(() => { /* ignore */ });
  }, []);

  const act = useCallback(async (eventId, action) => {
    setActing(eventId);
    try {
      await adminMarketAbuseApi.resolve(eventId, action);
      await load();
    } catch (_e) { /* ignore */ }
    finally { setActing(null); }
  }, [load]);

  const submitDemo = useCallback(async () => {
    if (!demoText.trim()) return;
    setDemoLoading(true);
    try {
      const r = await guardApi.scan(demoText, "admin-demo");
      setDemoResult(r.data);
      await load();
    } catch (_e) { /* ignore */ }
    finally { setDemoLoading(false); }
  }, [demoText, load]);

  const stats = useMemo(() => {
    const by = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const it of items) {
      const worst = (it.matches || []).reduce((a, b) => {
        const rank = { low: 1, medium: 2, high: 3, critical: 4 };
        return rank[b.severity] > rank[a?.severity || "low"] ? b : a;
      }, null);
      if (worst?.severity) by[worst.severity] = (by[worst.severity] || 0) + 1;
    }
    return by;
  }, [items]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.header}>
        <div>
          <h2 style={S.title} data-testid={`${testid}-title`}>Market Abuse Awareness Center™</h2>
          <p style={S.subtitle}>DEFENSIVE intelligence only — detect, review, and resolve flagged events. No abuse facilitation.</p>
        </div>
        <div style={S.statsRow} data-testid={`${testid}-stats`}>
          {Object.entries(stats).map(([k, v]) => (
            <span key={k} style={{ ...S.statPill, color: SEVERITY_COLOR[k], borderColor: SEVERITY_COLOR[k] }} data-testid={`${testid}-stat-${k}`}>
              {k.toUpperCase()}: {v}
            </span>
          ))}
        </div>
      </header>

      <div style={S.toolbar} data-testid={`${testid}-toolbar`}>
        <Tab active={!resolved} onClick={() => setResolved(false)} testid={`${testid}-tab-pending`}>Pending</Tab>
        <Tab active={resolved}  onClick={() => setResolved(true)}  testid={`${testid}-tab-resolved`}>Resolved</Tab>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={S.select} data-testid={`${testid}-severity-filter`}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <button type="button" onClick={load} style={S.refresh} data-testid={`${testid}-refresh`}>Refresh</button>
      </div>

      <div style={S.demoCard} data-testid={`${testid}-demo`}>
        <h3 style={S.demoTitle}>Defensive Scan Test</h3>
        <textarea
          value={demoText}
          onChange={(e) => setDemoText(e.target.value)}
          rows={2}
          maxLength={1200}
          placeholder="Paste any user query / claim / statement to run the defensive Market Abuse Guard™ over it. Tries detection of MNPI, pump-and-dump, spoofing, manipulation, synthetic fraud, etc."
          style={S.textarea}
          data-testid={`${testid}-demo-input`}
        />
        <div style={S.demoActions}>
          <button type="button" onClick={submitDemo} disabled={demoLoading} style={S.demoBtn} data-testid={`${testid}-demo-submit`}>
            {demoLoading ? "Scanning…" : "Run defensive scan"}
          </button>
          {demoResult && (
            <span style={{ ...S.demoVerdict, color: demoResult.verdict === "clean" ? PALETTE.greenAccent : (demoResult.verdict === "blocked" ? PALETTE.red : PALETTE.amber) }} data-testid={`${testid}-demo-verdict`}>
              Verdict: {demoResult.verdict.toUpperCase()} · Action: {demoResult.action}
              {demoResult.matches?.length > 0 && ` · ${demoResult.matches.map((m) => m.category).join(", ")}`}
            </span>
          )}
        </div>
        {catalog && (
          <div style={S.catalogChips} data-testid={`${testid}-catalog-chips`}>
            {catalog.categories?.map((c) => <span style={S.chip} key={c}>{c}</span>)}
          </div>
        )}
      </div>

      {error && <div style={S.err} data-testid={`${testid}-error`}>{error}</div>}
      {loading && <div style={S.loading}>Loading flagged events…</div>}
      {!loading && !error && items.length === 0 && (
        <div style={S.empty} data-testid={`${testid}-empty`}>
          {resolved ? "No resolved events." : "No pending events. ORACLE Market Abuse Guard is calm."}
        </div>
      )}

      <ul style={S.list}>
        {items.map((it) => {
          const worst = (it.matches || []).reduce((a, b) => {
            const rank = { low: 1, medium: 2, high: 3, critical: 4 };
            return rank[b.severity] > rank[(a || {}).severity || "low"] ? b : a;
          }, null);
          const color = SEVERITY_COLOR[worst?.severity || "low"];
          return (
            <motion.li
              key={it.event_id}
              style={{ ...S.item, borderColor: color }}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`${testid}-item-${it.event_id}`}
            >
              <header style={S.itemHead}>
                <span style={{ ...S.severityPill, color, borderColor: color }} data-testid={`${testid}-item-${it.event_id}-severity`}>
                  {(worst?.severity || "low").toUpperCase()}
                </span>
                <span style={S.itemVerdict}>{(it.verdict || "—").toUpperCase()}</span>
                <span style={S.itemAction}>{(it.action || "—").replace("_", " ")}</span>
                <span style={S.itemTime}>{it.timestamp}</span>
              </header>
              <pre style={S.itemText}>{it.text_snippet}</pre>
              <div style={S.itemMatches}>
                {(it.matches || []).map((m, i) => (
                  <span key={i} style={{ ...S.matchChip, color: SEVERITY_COLOR[m.severity], borderColor: SEVERITY_COLOR[m.severity] }}>
                    {m.category}: “{m.match}”
                  </span>
                ))}
              </div>
              {!resolved && (
                <div style={S.itemActions}>
                  <button type="button" onClick={() => act(it.event_id, "reviewed")} disabled={acting === it.event_id} style={{ ...S.actBtn, ...S.actReview }} data-testid={`${testid}-item-${it.event_id}-review`}>Review</button>
                  <button type="button" onClick={() => act(it.event_id, "dismissed")} disabled={acting === it.event_id} style={{ ...S.actBtn, ...S.actDismiss }} data-testid={`${testid}-item-${it.event_id}-dismiss`}>Dismiss</button>
                  <button type="button" onClick={() => act(it.event_id, "escalated")} disabled={acting === it.event_id} style={{ ...S.actBtn, ...S.actEscalate }} data-testid={`${testid}-item-${it.event_id}-escalate`}>Escalate</button>
                  <button type="button" onClick={() => act(it.event_id, "locked")}    disabled={acting === it.event_id} style={{ ...S.actBtn, ...S.actLock }}    data-testid={`${testid}-item-${it.event_id}-lock`}>Lock Output</button>
                  <button type="button" onClick={() => act(it.event_id, "hitl_required")} disabled={acting === it.event_id} style={{ ...S.actBtn, ...S.actHitl }} data-testid={`${testid}-item-${it.event_id}-hitl`}>HITL</button>
                </div>
              )}
            </motion.li>
          );
        })}
      </ul>

      <footer style={S.foot}>DEFENSIVE only. ORACLE never facilitates unlawful activity. All events audited.</footer>
    </section>
  );
}

const S = {
  shell:        { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 12, color: PALETTE.slate3 },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  title:        { margin: 0, color: PALETTE.goldBright, fontSize: 16, letterSpacing: "0.05em" },
  subtitle:     { margin: "4px 0 0 0", color: PALETTE.slate4, fontSize: 12, lineHeight: 1.4 },
  statsRow:     { display: "flex", gap: 6, flexWrap: "wrap" },
  statPill:     { fontFamily: "'Space Mono', monospace", fontSize: 10, padding: "3px 8px", borderRadius: 999, border: "1px solid", letterSpacing: "0.10em" },
  toolbar:      { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
  tab:          { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  tabActive:    { background: PALETTE.gold, color: PALETTE.bgDeep, borderColor: PALETTE.gold, fontWeight: 800 },
  select:       { background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "5px 9px", borderRadius: 8, fontSize: 12, fontFamily: "inherit" },
  refresh:      { appearance: "none", background: "transparent", border: `1px dashed ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  demoCard:     { background: "rgba(197,160,89,0.04)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  demoTitle:    { margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.12em" },
  textarea:     { width: "100%", boxSizing: "border-box", background: "#040608", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, borderRadius: 8, padding: 8, fontSize: 12.5, fontFamily: "inherit", resize: "vertical" },
  demoActions:  { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  demoBtn:      { appearance: "none", background: PALETTE.gold, color: PALETTE.bgDeep, border: "none", padding: "7px 14px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontWeight: 800, letterSpacing: "0.08em", cursor: "pointer" },
  demoVerdict:  { fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.04em" },
  catalogChips: { display: "flex", flexWrap: "wrap", gap: 4 },
  chip:         { fontFamily: "'Space Mono', monospace", fontSize: 10, color: PALETTE.slate4, padding: "2px 7px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, background: "rgba(255,255,255,0.02)" },
  err:          { color: PALETTE.red, fontSize: 12.5, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8 },
  loading:      { color: PALETTE.slate4, fontSize: 12.5 },
  empty:        { color: PALETTE.slate4, fontSize: 13, padding: 12, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 10 },
  list:         { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 },
  item:         { background: "#040608", border: "1px solid", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  itemHead:     { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  severityPill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 8px", borderRadius: 999, border: "1px solid" },
  itemVerdict:  { fontFamily: "'Space Mono', monospace", fontSize: 11, color: PALETTE.gold, letterSpacing: "0.08em" },
  itemAction:   { fontFamily: "'Space Mono', monospace", fontSize: 11, color: PALETTE.slate4 },
  itemTime:     { fontFamily: "'Space Mono', monospace", fontSize: 10, color: PALETTE.slate5, marginLeft: "auto" },
  itemText:     { margin: 0, color: PALETTE.slate3, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", fontFamily: "inherit" },
  itemMatches:  { display: "flex", flexWrap: "wrap", gap: 4 },
  matchChip:    { fontFamily: "'Space Mono', monospace", fontSize: 10, padding: "2px 7px", borderRadius: 999, border: "1px solid", background: "rgba(255,255,255,0.02)" },
  itemActions:  { display: "flex", gap: 6, flexWrap: "wrap" },
  actBtn:       { appearance: "none", padding: "5px 11px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", cursor: "pointer" },
  actReview:    { background: "transparent", color: PALETTE.greenAccent, border: `1px solid ${PALETTE.greenAccent}` },
  actDismiss:   { background: "transparent", color: PALETTE.slate4, border: `1px solid ${PALETTE.slate4}` },
  actEscalate:  { background: "transparent", color: PALETTE.amber, border: `1px solid ${PALETTE.amber}` },
  actLock:      { background: "transparent", color: PALETTE.red, border: `1px solid ${PALETTE.red}` },
  actHitl:      { background: "transparent", color: PALETTE.gold, border: `1px solid ${PALETTE.gold}` },
  foot:         { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
